import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import FotoUpload from '../components/FotoUpload';
import { SkeletonProductCard } from '../components/Skeleton';
import { useToast } from '../components/ToastContext';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSubscription } from '../hooks/useSubscription';
import comprimeImmagine from '../utils/comprimeImmagine';
import AnnuncioAIModal from '../components/AnnuncioAIModal';
import ProGate from '../components/ProGate';
const CATEGORIES = ['Scarpe', 'Abbigliamento', 'Profumi', 'Tech', 'Borse'];
const CONDITIONS = ['Nuovo', 'Ottime', 'Buone', 'Discrete'];

const emptyForm = { name: '', description: '', price: '', quantity: '1', category: '', condition: '', notes: '',
  brand: '', taglia: '', colore: '', prezzo_acquisto: '' };

export default function Products() {
  const { isPro } = useSubscription();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, current_page: 1 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formFotos, setFormFotos] = useState([]);
  const [formFotoPreviews, setFormFotoPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const formFotoRef = useRef();
  const toast = useToast();
  const navTo = useNavigate();
  const [annuncioAIProdotto, setAnnuncioAIProdotto] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name}
  const [showArchived, setShowArchived] = useState(false);

  // AI flow states
  const [showAddModal, setShowAddModal] = useState(false);
  const [aiStep, setAiStep] = useState(0); // 0=hidden, 1=upload, 2=loading, 3=results
  const [aiFotos, setAiFotos] = useState([]);
  const [aiFotoPreviews, setAiFotoPreviews] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [aiForm, setAiForm] = useState(emptyForm);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiLoadingText, setAiLoadingText] = useState(0);
  const [aiModified, setAiModified] = useState({});
  const [nomeHint, setNomeHint] = useState('');
  const aiFotoRef = useRef();
  const AI_TEXTS = ['🔍 Identifico il prodotto...', '🏷️ Riconosco il brand...', '💰 Stimo il valore di mercato...', '📝 Scrivo la descrizione...', '✅ Quasi pronto...'];

  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [orderBy, setOrderBy] = useState('data_aggiunta');
  const [orderDir, setOrderDir] = useState('desc');
  const [page, setPage] = useState(1);
  const searchTimer = useRef();
  const tableRef = useRef(null);

  const isMobile = useIsMobile();

  const loadProducts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterCat) params.set('categoria', filterCat);
    if (filterStatus) params.set('status', filterStatus);
    params.set('order_by', orderBy);
    params.set('order_dir', orderDir);
    params.set('page', page);
    params.set('per_page', 12);
    api.get(`/products?${params}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [search, filterCat, filterStatus, orderBy, orderDir, page]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleSearchInput = (val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 300);
  };

  const hasFilters = search || filterCat || filterStatus || orderBy !== 'data_aggiunta';
  const resetFilters = () => { setSearch(''); setFilterCat(''); setFilterStatus(''); setOrderBy('data_aggiunta'); setOrderDir('desc'); setPage(1); };

  const addFormFotos = (newFiles) => {
    const fileArray = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (fileArray.length === 0) return;
    const newPreviews = [];
    fileArray.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        newPreviews.push(ev.target.result);
        if (newPreviews.length === fileArray.length) setFormFotoPreviews((prev) => [...prev, ...newPreviews]);
      };
      reader.readAsDataURL(f);
    });
    setFormFotos((prev) => [...prev, ...fileArray]);
  };
  const handleFormFoto = (e) => { addFormFotos(e.target.files); if (formFotoRef.current) formFotoRef.current.value = ''; };
  const handleFormDrop = (e) => { e.preventDefault(); setDragOver(false); addFormFotos(e.dataTransfer.files); };
  const removeFormFoto = (idx) => {
    setFormFotos((prev) => prev.filter((_, i) => i !== idx));
    setFormFotoPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    try {
      const res = await api.post('/products', {
        name: form.name, description: form.description,
        price: parseFloat(form.price) || 0, quantity: parseInt(form.quantity) || 1,
        category: form.category, condition: form.condition, notes: form.notes,
        brand: form.brand, taglia: form.taglia, colore: form.colore,
        prezzo_acquisto: form.prezzo_acquisto ? parseFloat(form.prezzo_acquisto) : null,
      });
      if (formFotos.length > 0 && res.data.id) {
        for (const file of formFotos) {
          const compressed = await comprimeImmagine(file, 4);
          const fd = new FormData();
          fd.append('foto', compressed);
          await api.post(`/products/${res.data.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      setForm(emptyForm); setFormFotos([]); setFormFotoPreviews([]);
      if (formFotoRef.current) formFotoRef.current.value = '';
      setShowForm(false); loadProducts(); toast.success('Prodotto aggiunto!');
    } catch (err) { setError(err.response?.data?.detail || 'Errore'); }
  };

  const badgeBase = { borderRadius:9999,padding:'4px 12px',fontSize:'0.68rem',fontWeight:700,fontFamily:'DM Sans',letterSpacing:'0.04em',textTransform:'uppercase',display:'inline-flex',alignItems:'center',gap:4 };
  const StatusBadge = ({ status }) => {
    if (status === 'venduto') return <span style={{ ...badgeBase, background:'#f87171', color:'#000' }}>● VENDUTO</span>;
    if (status === 'in_vendita') return <span style={{ ...badgeBase, background:'#22d3ee', color:'#000' }}>● IN VENDITA</span>;
    return <span style={{ ...badgeBase, background:'#4ade80', color:'#000' }}>● DISPONIBILE</span>;
  };
  const statusBadgeClass = (s) => {
    if (s === 'venduto') return 'badge badge-red';
    if (s === 'in_vendita') return 'badge badge-cyan';
    return 'badge badge-green';
  };
  const statusLabel = (s) => s === 'venduto' ? 'Venduto' : s === 'in_vendita' ? 'In Vendita' : 'Disponibile';

  // AI foto handlers
  const addAiFotos = (newFiles) => {
    const fileArray = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (fileArray.length === 0) return;
    const newPrevs = [];
    fileArray.forEach(f => {
      const reader = new FileReader();
      reader.onload = ev => {
        newPrevs.push(ev.target.result);
        if (newPrevs.length === fileArray.length) setAiFotoPreviews(prev => [...prev, ...newPrevs]);
      };
      reader.readAsDataURL(f);
    });
    setAiFotos(prev => [...prev, ...fileArray]);
  };
  const removeAiFoto = (idx) => {
    setAiFotos(prev => prev.filter((_, i) => i !== idx));
    setAiFotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAiAnalyze = async () => {
    if (aiFotos.length === 0) return;
    setAiStep(2);
    setAiLoadingText(0);
    const interval = setInterval(() => setAiLoadingText(p => (p + 1) % AI_TEXTS.length), 2500);
    try {
      console.log('[AI Inventario] Avvio analisi con', aiFotos.length, 'foto, hint:', nomeHint);
      const fd = new FormData();
      for (const f of aiFotos) {
        const compressed = await comprimeImmagine(f, 4);
        fd.append('photos', compressed);
        console.log('[AI Inventario] Foto:', compressed.name, compressed.type, compressed.size, f.size !== compressed.size ? '(compressa)' : '');
      }
      if (nomeHint.trim()) fd.append('nome_hint', nomeHint.trim());
      const res = await api.post('/inventory/analizza-foto', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      console.log('[AI Inventario] Risposta:', res.data);
      setAiResult(res.data);
      setAiForm({
        name: res.data.nome || '', brand: res.data.brand || '', category: res.data.categoria || '',
        condition: res.data.condizione || '', price: String(res.data.prezzo_consigliato || ''),
        description: res.data.descrizione || '', colore: res.data.colore || '',
        taglia: res.data.taglia || '', notes: res.data.note || '', quantity: '1', prezzo_acquisto: '',
      });
      setAiModified({});
      setAiStep(3);
    } catch (err) {
      console.error('[AI Inventario] Errore:', err.response?.data || err.message || err);
      let msg = err.response?.data?.detail || err.message || 'Errore analisi AI';
      if (msg.includes('exceeds') && msg.includes('MB')) msg = 'Foto troppo grande. Riprova — la compressione automatica la ridurra.';
      toast.error(`❌ ${msg}`);
      setAiStep(1);
    } finally { clearInterval(interval); }
  };

  const handleAiSave = async () => {
    setAiSaving(true);
    try {
      const res = await api.post('/products', {
        name: aiForm.name, description: aiForm.description,
        price: parseFloat(aiForm.price) || 0, quantity: parseInt(aiForm.quantity) || 1,
        category: aiForm.category, condition: aiForm.condition, notes: aiForm.notes,
        brand: aiForm.brand, taglia: aiForm.taglia, colore: aiForm.colore,
        prezzo_acquisto: aiForm.prezzo_acquisto ? parseFloat(aiForm.prezzo_acquisto) : null,
      });
      if (aiFotos.length > 0 && res.data.id) {
        for (const file of aiFotos) {
          const compressed = await comprimeImmagine(file, 4);
          const fd = new FormData();
          fd.append('foto', compressed);
          await api.post(`/products/${res.data.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      toast.success('Prodotto aggiunto!');
      setAiStep(0); setShowAddModal(false); setAiFotos([]); setAiFotoPreviews([]); setAiResult(null);
      loadProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore salvataggio'); }
    finally { setAiSaving(false); }
  };

  // Live preview card
  const PreviewCard = () => (
    <div className="card" style={{ position: 'sticky', top: 24, padding: 0, overflow: 'hidden' }}>
      <p className="label" style={{ padding: '12px 16px 0' }}>Anteprima</p>
      <div style={{ aspectRatio: '4/3', background: T.elem, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {formFotoPreviews.length > 0 ? <img src={formFotoPreviews[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ fontSize: '2rem', color: T.faint }}>📷</span>}
      </div>
      <div style={{ padding: 14 }}>
        {form.brand && <p style={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, color: T.violet, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{form.brand}</p>}
        <p style={{ fontFamily: T.fontDisplay, fontSize: '0.95rem', fontWeight: 600, color: T.text }}>{form.name || 'Nome prodotto'}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: '1.1rem', fontWeight: 800, color: T.text }}>{form.price ? `€${form.price}` : '€0.00'}</span>
          {form.prezzo_acquisto && <span style={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.green }}>acq. €{form.prezzo_acquisto}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {form.taglia && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 8px', fontFamily: T.fontBody, fontSize: '0.72rem', color: T.text, fontWeight: 600 }}>{form.taglia}</span>}
          {form.category && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 8px', fontFamily: T.fontBody, fontSize: '0.72rem', color: T.muted }}>{form.category}</span>}
        </div>
      </div>
    </div>
  );

  // Divider for form sections
  const SectionDivider = ({ text }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0 16px' }}>
      <div style={{ flex: 1, height: 1, background: T.border }} />
      <span style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '0 12px' }}>{text}</span>
      <div style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );

  return (
    <Layout>
      <main className="main-content" style={{ maxWidth: 1200, margin: '0 auto', paddingTop: 32 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 className="page-title">Il mio Inventario</h1>
            <p className="section-subtitle">{data.total} prodotti</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: T.card, borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              <button onClick={() => setViewMode('grid')} style={{ padding: '6px 12px', fontSize: '0.85rem', background: viewMode === 'grid' ? T.violetDark : 'transparent', color: viewMode === 'grid' ? 'white' : T.muted, border: 'none', cursor: 'pointer' }}>⊞</button>
              <button onClick={() => setViewMode('list')} style={{ padding: '6px 12px', fontSize: '0.85rem', background: viewMode === 'list' ? T.violetDark : 'transparent', color: viewMode === 'list' ? 'white' : T.muted, border: 'none', cursor: 'pointer' }}>☰</button>
            </div>
            <button onClick={() => { if (showForm) setShowForm(false); else setShowAddModal(true); }} className="btn-primary" style={isMobile ? { fontSize: '0.82rem', padding: '8px 14px' } : {}}>
              {showForm ? 'Annulla' : '+ Aggiungi'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 6 : 8, marginBottom: 24 }}>
          <input type="text" placeholder="🔍 Cerca prodotto..." defaultValue={search} onChange={(e) => handleSearchInput(e.target.value)} className="input" style={{ flex: 1, minWidth: isMobile ? 140 : 200, ...(isMobile ? { fontSize: '0.72rem', padding: '6px 10px', minHeight: 34 } : {}) }} />
          <select value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setPage(1); }} className="input" style={{ width: 'auto', ...(isMobile ? { fontSize: '0.72rem', padding: '6px 10px', borderRadius: 9999, minHeight: 34 } : {}) }}>
            <option value="">Tutte le categorie</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} className="input" style={{ width: 'auto', ...(isMobile ? { fontSize: '0.72rem', padding: '6px 10px', borderRadius: 9999, minHeight: 34 } : {}) }}>
            <option value="">Tutti gli stati</option>
            <option value="disponibile">Disponibile</option>
            <option value="in_vendita">In Vendita</option>
            <option value="venduto">Venduto</option>
          </select>
          <select value={`${orderBy}_${orderDir}`} onChange={(e) => { const [ob, od] = e.target.value.split('_'); setOrderBy(ob); setOrderDir(od); setPage(1); }} className="input" style={{ width: 'auto', ...(isMobile ? { fontSize: '0.72rem', padding: '6px 10px', borderRadius: 9999, minHeight: 34 } : {}) }}>
            <option value="data_aggiunta_desc">Piu recenti</option>
            <option value="prezzo_asc">Prezzo ↑</option>
            <option value="prezzo_desc">Prezzo ↓</option>
            <option value="nome_asc">Nome A-Z</option>
          </select>
          {hasFilters && <button onClick={resetFilters} className="btn-ghost">Reset</button>}
        </div>

        {/* ADD FORM (Step 6 redesign) */}
        {showForm && (
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <span style={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.muted }}>Inventario →</span>
              <span style={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.text }}>Aggiungi</span>
            </div>
            <h2 className="page-title" style={{ marginBottom: 24 }}>➕ Nuovo Prodotto</h2>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr', gap: isMobile ? 16 : 24 }}>
              {/* LEFT - Form */}
              <form onSubmit={handleSubmit}>
                {error && <div style={{ background: T.redGlow, border: `1px solid ${T.red}`, borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: T.red, fontFamily: T.fontBody, fontSize: '0.85rem' }}>{error}</div>}

                <SectionDivider text="Informazioni" />
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div><label className="label">Nome *</label><input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                  <div><label className="label">Brand</label><input type="text" className="input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="es. Nike" /></div>
                  <div><label className="label">Categoria *</label><select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required><option value="">Seleziona...</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
                  <div style={isMobile ? { gridColumn: '1 / -1' } : {}}><label className="label">Condizione *</label><select className="input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} required><option value="">Seleziona...</option>{CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div><label className="label">Taglia</label><input type="text" className="input" value={form.taglia} onChange={(e) => setForm({ ...form, taglia: e.target.value })} placeholder="es. 42, M" /></div>
                  <div><label className="label">Colore</label><input type="text" className="input" value={form.colore} onChange={(e) => setForm({ ...form, colore: e.target.value })} /></div>
                </div>

                <SectionDivider text="Prezzi" />
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
                  <div style={isMobile ? { gridColumn: '1 / -1' } : {}}><label className="label">Prezzo acquisto € *</label><input type="number" step="0.01" className="input" value={form.prezzo_acquisto} onChange={(e) => setForm({ ...form, prezzo_acquisto: e.target.value })} required /></div>
                  <div><label className="label">Prezzo vendita €</label><input type="number" step="0.01" className="input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
                  <div><label className="label">Quantita</label><input type="number" className="input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                </div>

                <SectionDivider text="Note" />
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div><label className="label">Descrizione</label><textarea className="input" style={{ resize: 'none' }} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div><label className="label">Note private</label><textarea className="input" style={{ resize: 'none' }} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>

                <SectionDivider text="Foto" />
                <input ref={formFotoRef} type="file" accept="image/*" multiple onChange={handleFormFoto} style={{ display: 'none' }} />
                {formFotoPreviews.length === 0 ? (
                  <>
                    {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
                      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', borderRadius: 9999, padding: '12px 16px', color: 'white', fontFamily: 'DM Sans', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44 }}>
                          📷 Scatta foto
                          <input type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handleFormFoto} />
                        </label>
                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 9999, padding: '12px 16px', color: '#e2e8f0', fontFamily: 'DM Sans', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44 }}>
                          🖼️ Galleria
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFormFoto} />
                        </label>
                      </div>
                    )}
                    <div onClick={() => formFotoRef.current?.click()} onDrop={handleFormDrop}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      style={{
                        border: `2px dashed ${dragOver ? T.violetDark : T.border}`, background: dragOver ? 'rgba(124,58,237,0.08)' : T.elem,
                        borderRadius: 12, padding: isMobile ? 20 : 40, textAlign: 'center', cursor: 'pointer', transition: '0.15s',
                      }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.5" style={{ margin: '0 auto 8px' }}><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                      <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.85rem' : '1rem', color: dragOver ? T.violet : T.muted }}>Trascina le foto qui</p>
                      <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.72rem' : '0.82rem', color: T.faint, marginTop: 4 }}>oppure clicca per selezionare — JPG PNG WEBP</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 100px)', gap: 8, marginBottom: 8 }}>
                      {formFotoPreviews.map((prev, i) => (
                        <div key={i} style={{ position: 'relative', width: 100, height: 100 }}>
                          <img src={prev} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, background: T.card }} />
                          <span style={{ position: 'absolute', top: 4, left: 4, fontSize: '0.65rem', fontWeight: 700, borderRadius: 4, background: i === 0 ? T.violetDark : T.muted, color: 'white', padding: '2px 6px' }}>
                            {i === 0 ? 'Cover' : i + 1}
                          </span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeFormFoto(i); }}
                            style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', background: T.red, color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => formFotoRef.current?.click()} className="btn-ghost">+ Aggiungi altre foto</button>
                  </div>
                )}

                <button type="submit" className="btn-primary" style={{ width: '100%', padding: 14, borderRadius: 14, fontSize: '1rem', marginTop: 24, justifyContent: 'center' }}>
                  💾 Salva nell'inventario
                </button>
              </form>

              {/* RIGHT - Preview (nascosta su mobile per ottimizzare spazio) */}
              {!isMobile && <PreviewCard />}
            </div>
          </div>
        )}

        {/* PRODUCTS GRID — FIX 3: smaller cards, 3-4 cols; FIX 2: DM Sans prices; FIX 4: sold rework */}
        {loading ? (
          <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 8 : 12 }}>
            {Array.from({ length: 8 }).map((_, i) => <SkeletonProductCard key={i} />)}
          </div>
        ) : (data?.items || []).length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <p style={{ color: T.muted, fontSize: '1rem' }}>{hasFilters ? 'Nessun risultato per i filtri selezionati.' : 'Nessun prodotto ancora.'}</p>
            {!hasFilters && <p style={{ color: T.faint, fontSize: '0.85rem', marginTop: 4 }}>Clicca "+ Aggiungi" per iniziare</p>}
          </div>
        ) : viewMode === 'grid' ? (
          <>
          {/* Prodotti disponibili */}
          <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 8 : 12 }}>
            {(data?.items || []).filter(p => p.status !== 'venduto' && p.status !== 'archiviato').map((p) => (
              <div key={p.id} style={{ background: 'linear-gradient(135deg,#7c3aed,#2d1f6e)', borderRadius: 17, padding: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.card, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: '0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(124,58,237,0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                  {/* FOTO — altezza fissa */}
                  <Link to={`/products/${p.id}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
                    <div style={{ position: 'relative', width: '100%', aspectRatio: isMobile ? '1/1' : '3/4', background: T.elem, overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
                      {p.foto_url ? <img src={`${API_BASE}${p.foto_url}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '2rem', opacity: 0.2 }}>📦</span></div>}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(transparent 50%, #151230 100%)', zIndex: 1 }} />
                      <span style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, ...badgeBase, background: p.status === 'venduto' ? '#f87171' : '#4ade80', color: '#000' }}>● {p.status === 'venduto' ? 'VENDUTO' : 'DISPONIBILE'}</span>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirm({ id: p.id, name: p.name }); }} style={{
                        position: 'absolute', top: 8, right: 8, zIndex: 3, width: isMobile ? 26 : 30, height: isMobile ? 26 : 30, borderRadius: 8,
                        background: 'rgba(248,113,113,0.2)', backdropFilter: 'blur(4px)', border: '1px solid rgba(248,113,113,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: '0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.5)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.2)'; }}
                      title="Elimina prodotto">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#f87171" stroke="none"><path d="M3 6h18v2H3V6zm2 3h14l-1.2 12.5a2 2 0 01-2 1.5H8.2a2 2 0 01-2-1.5L5 9zm4 2v8h2v-8H9zm4 0v8h2v-8h-2zM8 4h8v2H8V4z"/></svg>
                      </button>
                    </div>
                  </Link>
                  {/* BODY — flex grow */}
                  <div style={{ padding: isMobile ? 8 : 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {p.brand && <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.6rem' : '0.65rem', fontWeight: 700, color: T.violet, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: isMobile ? 2 : 0 }}>{p.brand}</p>}
                    <Link to={`/products/${p.id}`} style={{ textDecoration: 'none' }}>
                      <p style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.72rem' : '0.82rem', fontWeight: 600, color: T.text, lineHeight: 1.3, height: isMobile ? '2.2em' : '2.4em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginTop: 4, marginBottom: 6 }}>{p.name}</p>
                    </Link>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.85rem' : '0.95rem', fontWeight: 800, color: T.text }}>€{(p.price || 0).toFixed(2)}</span>
                      {p.prezzo_acquisto != null && <span style={{ fontFamily: T.fontBody, fontSize: isMobile ? '0.62rem' : '0.72rem', fontWeight: 600, color: T.green }}>acq. €{(p.prezzo_acquisto || 0).toFixed(2)}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden', height: 22, flexShrink: 0 }}>
                      {p.taglia && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: isMobile ? '1px 5px' : '2px 5px', fontFamily: T.fontBody, fontSize: isMobile ? '0.58rem' : '0.65rem', color: T.text, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.taglia}</span>}
                      {p.category && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: isMobile ? '1px 5px' : '2px 5px', fontFamily: T.fontBody, fontSize: isMobile ? '0.58rem' : '0.65rem', color: T.muted, whiteSpace: 'nowrap' }}>{p.category}</span>}
                    </div>
                    {/* Spacer */}
                    <div style={{ flex: 1 }} />
                    {/* Bottoni Modifica/Analizza */}
                    <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
                      <Link to={`/products/${p.id}`} style={{
                        flex: 1, textAlign: 'center', padding: isMobile ? '5px 0' : '7px 0', background: T.elem, border: `1px solid ${T.border}`,
                        borderRadius: 20, fontFamily: T.fontBody, fontSize: isMobile ? '0.65rem' : '0.75rem', color: T.text, textDecoration: 'none', transition: '0.15s', minHeight: isMobile ? 30 : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = T.violet}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                        ✏️ Modifica
                      </Link>
                      <button onClick={(e) => {
                        e.preventDefault();
                        localStorage.setItem('analisi_prefill', JSON.stringify({ nome: p.name, categoria: p.category, descrizione: p.description, foto_urls: p.photos?.map(ph => ph.foto_url) || (p.foto_url ? [p.foto_url] : []), product_id: p.id }));
                        navTo('/analisi-mercato');
                      }} style={{
                        flex: 1, textAlign: 'center', padding: isMobile ? '5px 0' : '7px 0', background: T.elem, border: `1px solid ${T.violetDark}`,
                        borderRadius: 20, fontFamily: T.fontBody, fontSize: isMobile ? '0.65rem' : '0.75rem', color: T.violet, transition: '0.15s', cursor: 'pointer', minHeight: isMobile ? 30 : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 12px rgba(124,58,237,0.3)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>
                        📊 Analizza
                      </button>
                    </div>
                  </div>
                  {/* FOOTER Annuncio AI */}
                  <div style={{ flexShrink: 0 }}>
                    <button onClick={(e) => { e.preventDefault(); isPro ? setAnnuncioAIProdotto(p) : navTo('/abbonamento'); }}
                      style={{ width: '100%', padding: isMobile ? '6px' : 8, background: isPro ? T.gradPrimary : T.elem, color: isPro ? 'white' : T.muted, border: isPro ? 'none' : `1px solid ${T.border}`, borderRadius: '0 0 16px 16px', fontFamily: T.fontBody, fontSize: isMobile ? '0.68rem' : '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                      {isPro ? '🤖 Annuncio AI' : '🔒 Annuncio AI (PRO)'}
                    </button>
                    {!p.foto_url && (
                      <div style={{ padding: '0 10px 8px' }}><FotoUpload productId={p.id} onUploaded={loadProducts} buttonLabel="📷 Foto" /></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sezione Venduti (esclusi archiviati) */}
          {(data?.items || []).filter(p => p.status === 'venduto').length > 0 && (
            <div style={{ marginTop: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Prodotti Venduti</h2>
                <span style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid #f87171', borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#f87171' }}>
                  {(data?.items || []).filter(p => p.status === 'venduto').length}
                </span>
                <div style={{ flex: 1, height: 1, background: 'rgba(248,113,113,0.2)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 8 : 12, opacity: 0.85 }}>
                {(data?.items || []).filter(p => p.status === 'venduto').map((p) => (
                  <div key={p.id} style={{ background: 'linear-gradient(135deg,rgba(248,113,113,0.15),#2d1f6e)', borderRadius: 17, padding: 1 }}>
                    <div style={{
                      background: 'linear-gradient(135deg,rgba(248,113,113,0.06),transparent)', borderRadius: 16,
                      overflow: 'hidden', cursor: 'pointer', transition: '0.2s',
                      border: '1px solid rgba(248,113,113,0.25)',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,113,113,0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                      <Link to={`/products/${p.id}`} style={{ textDecoration: 'none' }}>
                        <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', background: T.elem, overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
                          {p.foto_url ? <img src={`${API_BASE}${p.foto_url}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '2rem', opacity: 0.2 }}>📦</span></div>}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(transparent, rgba(13,11,30,0.7))', zIndex: 1 }} />
                          <span style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, ...badgeBase, background: '#f87171', color: '#000' }}>● VENDUTO</span>
                        </div>
                      </Link>
                      <div style={{ padding: 10 }}>
                        {p.brand && <p style={{ fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 700, color: T.violet, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{p.brand}</p>}
                        <Link to={`/products/${p.id}`} style={{ textDecoration: 'none' }}>
                          <p className="line-clamp-2" style={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text }}>{p.name}</p>
                        </Link>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
                          <span style={{ fontFamily: T.fontBody, fontSize: '0.95rem', fontWeight: 800, color: '#6b7280', textDecoration: 'line-through' }}>€{(p.price || 0).toFixed(2)}</span>
                          {p.prezzo_acquisto != null && <span style={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.green }}>acq. €{(p.prezzo_acquisto || 0).toFixed(2)}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                          {p.taglia && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 5px', fontFamily: T.fontBody, fontSize: '0.65rem', color: T.text, fontWeight: 600 }}>{p.taglia}</span>}
                          {p.category && <span style={{ background: T.elem, border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 5px', fontFamily: T.fontBody, fontSize: '0.65rem', color: T.muted }}>{p.category}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', width: '100%', gap: 0 }}>
                        <div style={{ flex: 1, padding: 8, background: 'rgba(248,113,113,0.08)', color: '#f87171', borderRadius: '0 0 0 16px', fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, textAlign: 'center' }}>
                          ✅ Venduto
                        </div>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation();
                          setDeleteConfirm({ id: p.id, name: p.name, action: 'archivia' });
                        }} style={{
                          padding: '8px 16px', background: 'rgba(167,139,250,0.12)', color: T.violet,
                          border: 'none', borderLeft: `1px solid ${T.border}`, borderRadius: '0 0 16px 0', cursor: 'pointer',
                          fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 700, transition: '0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(167,139,250,0.25)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(167,139,250,0.12)'; }}
                        title="Archivia">
                          📦 Archivia
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        ) : (
          /* List View */
          <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
            {isMobile && (
              <>
                <button onClick={() => tableRef.current?.scrollBy({ left: -150, behavior: 'smooth' })} style={{
                  position:'absolute', left:0, top:'50%', transform:'translateY(-50%)',
                  zIndex:10, background:'rgba(13,11,30,0.9)', border:'1px solid #2d1b69',
                  borderRadius:'50%', width:32, height:32, display:'flex',
                  alignItems:'center', justifyContent:'center', cursor:'pointer',
                  color:'#a78bfa', fontSize:'1rem'
                }}>{'\u2190'}</button>
                <button onClick={() => tableRef.current?.scrollBy({ left: 150, behavior: 'smooth' })} style={{
                  position:'absolute', right:0, top:'50%', transform:'translateY(-50%)',
                  zIndex:10, background:'rgba(13,11,30,0.9)', border:'1px solid #2d1b69',
                  borderRadius:'50%', width:32, height:32, display:'flex',
                  alignItems:'center', justifyContent:'center', cursor:'pointer',
                  color:'#a78bfa', fontSize:'1rem'
                }}>{'\u2192'}</button>
              </>
            )}
            <div ref={tableRef} style={{
              overflowX: isMobile ? 'auto' : 'visible',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}>
            <table style={{ width: '100%', minWidth: isMobile ? 600 : '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.elem, borderBottom: `1px solid ${T.border}` }}>
                  {['Foto', 'Prodotto', 'Categoria', 'Stato', 'Acquistato', 'Prezzo', 'Azioni'].map((h) => (
                    <th key={h} style={{ fontFamily: T.fontBody, fontSize: '0.72rem', textTransform: 'uppercase', color: T.muted, padding: '10px 14px', textAlign: h === 'Prezzo' || h === 'Acquistato' ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((p) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid rgba(45,31,110,0.3)`, transition: '0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '8px 14px' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', background: T.elem }}>
                        {p.foto_url ? <img src={`${API_BASE}${p.foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>📦</div>}
                      </div>
                    </td>
                    <td style={{ padding: '8px 14px' }}>
                      <Link to={`/products/${p.id}`} style={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 500, color: T.text, textDecoration: 'none' }}>{p.name}</Link>
                      {p.brand && <p style={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.muted }}>{p.brand}</p>}
                    </td>
                    <td style={{ padding: '8px 14px', fontFamily: T.fontBody, fontSize: '0.85rem', color: T.muted }}>{p.category || '—'}</td>
                    <td style={{ padding: '8px 14px' }}><span className={statusBadgeClass(p.status)}>{statusLabel(p.status)}</span></td>
                    <td style={{ padding: '8px 14px', fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text, textAlign: 'right' }}>{p.prezzo_acquisto != null ? `€${p.prezzo_acquisto.toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '8px 14px', fontFamily: T.fontDisplay, fontSize: '0.9rem', fontWeight: 700, color: T.violet, textAlign: 'right' }}>€{p.price.toFixed(2)}</td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <Link to={`/products/${p.id}`} className="btn-ghost" style={{ fontSize: '0.75rem' }}>✏️</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {data.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="btn-secondary" style={{ opacity: page <= 1 ? 0.3 : 1 }}>← Prev</button>
            {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={{
                padding: '6px 12px', borderRadius: 8, fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                background: p === page ? T.violetDark : T.elem,
                color: p === page ? 'white' : T.muted, fontWeight: p === page ? 700 : 400,
              }}>{p}</button>
            ))}
            <button onClick={() => setPage(Math.min(data.pages, page + 1))} disabled={page >= data.pages} className="btn-secondary" style={{ opacity: page >= data.pages ? 0.3 : 1 }}>Next →</button>
          </div>
        )}
      </main>

      {/* Modal scelta modalità aggiunta */}
      {showAddModal && aiStep === 0 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(13,11,30,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}>
          <div style={{ background: '#151230', border: '1px solid #2d1b69', borderRadius: 24, padding: 32, maxWidth: 480, width: '100%', position: 'relative', animation: 'fadeInUp 0.3s ease-out' }}>
            <button onClick={() => setShowAddModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: T.muted, fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: '#e2e8f0', marginBottom: 4 }}>➕ Aggiungi Prodotto</h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#6b7280', marginBottom: 24 }}>Come vuoi aggiungere il prodotto?</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Manuale */}
              <div onClick={() => { setShowAddModal(false); setShowForm(true); }} style={{
                background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 20, padding: 24,
                cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2d1b69'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}>
                <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: 12 }}>📝</span>
                <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>Manuale</p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#6b7280', marginTop: 6 }}>Compila tu tutti i dettagli del prodotto</p>
              </div>
              {/* AI */}
              <div onClick={() => { if (!isPro) return; setAiStep(1); setAiFotos([]); setAiFotoPreviews([]); setAiResult(null); }} style={{
                background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,70,229,0.08))', border: '1px solid #7c3aed',
                borderRadius: 20, padding: 24, cursor: isPro ? 'pointer' : 'not-allowed', textAlign: 'center', transition: 'all 0.15s',
                boxShadow: '0 0 20px rgba(124,58,237,0.2)', opacity: isPro ? 1 : 0.5, position: 'relative',
              }}
                onMouseEnter={e => { if (isPro) { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(124,58,237,0.35)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.boxShadow = '0 0 20px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = ''; }}>
                {!isPro && <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(124,58,237,0.9)', borderRadius: 9999, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700, color: 'white' }}>🔒 PRO</div>}
                <span style={{ display: 'inline-block', background: '#7c3aed', color: 'white', borderRadius: 9999, fontSize: '0.65rem', padding: '3px 10px', marginBottom: 8, fontWeight: 700 }}>✨ Consigliato</span>
                <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: 12 }}>🤖</span>
                <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#a78bfa' }}>Genera con AI</p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#6b7280', marginTop: 6 }}>Carica le foto e l'AI compila tutto</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Step 1 — Upload foto */}
      {showAddModal && aiStep === 1 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(13,11,30,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#151230', border: '1px solid #2d1b69', borderRadius: 20, padding: 28, maxWidth: 560, width: '100%', animation: 'fadeInUp 0.3s ease-out' }}>
            <button onClick={() => { setAiStep(0); }} style={{ float: 'right', background: 'none', border: 'none', color: T.muted, fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 800, color: '#e2e8f0', marginBottom: 4 }}>📷 Carica le foto del prodotto</h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#6b7280', marginBottom: 20 }}>L'AI analizzerà le foto e compilerà automaticamente tutti i dettagli</p>

            <input ref={aiFotoRef} type="file" accept="image/*" multiple onChange={(e) => { addAiFotos(e.target.files); if (aiFotoRef.current) aiFotoRef.current.value = ''; }} style={{ display: 'none' }} />

            {aiFotoPreviews.length === 0 ? (
              <>
                {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', borderRadius: 9999, padding: '12px 16px', color: 'white', fontFamily: 'DM Sans', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44 }}>
                      📷 Scatta foto
                      <input type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={(e) => { addAiFotos(e.target.files); e.target.value = ''; }} />
                    </label>
                    <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 9999, padding: '12px 16px', color: '#e2e8f0', fontFamily: 'DM Sans', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44 }}>
                      🖼️ Galleria
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { addAiFotos(e.target.files); e.target.value = ''; }} />
                    </label>
                  </div>
                )}
                <div onClick={() => aiFotoRef.current?.click()}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); addAiFotos(e.dataTransfer.files); }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  style={{
                    border: `2px dashed ${dragOver ? '#7c3aed' : '#2d1b69'}`, background: dragOver ? 'rgba(124,58,237,0.08)' : '#1c1640',
                    borderRadius: 16, padding: 48, textAlign: 'center', cursor: 'pointer', transition: '0.15s',
                  }}>
                  <span style={{ fontSize: isMobile ? '1.8rem' : '3rem', display: 'block', color: '#4b5563', marginBottom: 8 }}>📷</span>
                  <p style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? '0.85rem' : '1rem', color: '#e2e8f0' }}>Trascina le foto qui</p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: isMobile ? '0.72rem' : '0.82rem', color: '#6b7280', marginTop: 6 }}>oppure clicca per selezionare • JPG, PNG, WebP • max 20 foto</p>
                </div>
              </>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(auto-fill, 80px)', gap: 8, marginBottom: 12 }}>
                  {aiFotoPreviews.map((prev, i) => (
                    <div key={i} style={{ position: 'relative', width: isMobile ? '100%' : 80, height: isMobile ? 70 : 80 }}>
                      <img src={prev} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
                      {i === 0 && <span style={{ position: 'absolute', top: 3, left: 3, fontSize: '0.6rem', fontWeight: 700, borderRadius: 4, background: '#7c3aed', color: 'white', padding: '1px 5px' }}>Cover</span>}
                      <button type="button" onClick={() => removeAiFoto(i)} style={{ position: 'absolute', top: 3, right: 3, width: 16, height: 16, borderRadius: '50%', background: '#f87171', color: 'white', border: 'none', fontSize: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => aiFotoRef.current?.click()} className="btn-ghost" style={{ fontSize: '0.82rem' }}>+ Aggiungi altre foto</button>
              </div>
            )}

            <div style={{ height: 1, background: '#2d1b69', margin: '20px 0' }} />
            <label className="label">📝 Nome prodotto (opzionale)</label>
            <input className="input" value={nomeHint} onChange={e => setNomeHint(e.target.value)}
              placeholder="es. Nike Air Jordan 1, iPhone 14 Pro, Borsa Louis Vuitton..." />
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: '#6b7280', marginTop: 6 }}>Aggiungere il nome aiuta l'AI a essere più precisa</p>

            <button onClick={handleAiAnalyze} disabled={aiFotos.length === 0} className="btn-primary"
              style={{ width: '100%', padding: 14, borderRadius: 16, fontSize: '1rem', marginTop: 20, justifyContent: 'center', opacity: aiFotos.length === 0 ? 0.5 : 1 }}>
              🤖 Analizza con AI
            </button>
            <button onClick={() => setAiStep(0)} className="btn-ghost" style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}>← Indietro</button>
          </div>
        </div>
      )}

      {/* AI Step 2 — Loading */}
      {showAddModal && aiStep === 2 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(13,11,30,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#151230', border: '1px solid #2d1b69', borderRadius: 20, padding: 40, maxWidth: 480, width: '100%', textAlign: 'center', animation: 'fadeInUp 0.3s ease-out' }}>
            <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 20px' }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'conic-gradient(#7c3aed, #4f46e5, #a78bfa, #7c3aed)', animation: 'spin 2s linear infinite' }} />
              <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', background: '#151230', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1.8rem' }}>🤖</span>
              </div>
            </div>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>L'AI sta analizzando le foto...</h3>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#a78bfa', marginBottom: 16, transition: 'opacity 0.3s' }}>{AI_TEXTS[aiLoadingText]}</p>
            <div style={{ width: '100%', height: 6, borderRadius: 3, background: '#1c1640', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#7c3aed,#4f46e5)', animation: 'progressBar 15s linear forwards' }} />
            </div>
          </div>
        </div>
      )}

      {/* AI Step 3 — Risultati + form modifica */}
      {showAddModal && aiStep === 3 && aiResult && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(13,11,30,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div style={{ background: '#151230', border: '1px solid #2d1b69', borderRadius: 20, padding: 28, maxWidth: 720, width: '100%', margin: '40px auto', animation: 'fadeInUp 0.3s ease-out' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: '1.3rem' }}>✅</span>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: '#4ade80', margin: 0 }}>Prodotto identificato!</h2>
            </div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: '#6b7280', marginBottom: 20 }}>Controlla e modifica i dati prima di salvare</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 20 }}>
              {/* LEFT — Form */}
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Nome {!aiModified.name && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <input className="input" value={aiForm.name} onChange={e => { setAiForm({...aiForm, name: e.target.value}); setAiModified({...aiModified, name: true}); }} />
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Brand {!aiModified.brand && aiForm.brand && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <input className="input" value={aiForm.brand} onChange={e => { setAiForm({...aiForm, brand: e.target.value}); setAiModified({...aiModified, brand: true}); }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Categoria {!aiModified.category && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <select className="input" value={aiForm.category} onChange={e => { setAiForm({...aiForm, category: e.target.value}); setAiModified({...aiModified, category: true}); }}>
                      <option value="">Seleziona...</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="Accessori">Accessori</option><option value="Sport">Sport</option><option value="Casa">Casa</option><option value="Altro">Altro</option>
                    </select>
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Condizione {!aiModified.condition && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <select className="input" value={aiForm.condition} onChange={e => { setAiForm({...aiForm, condition: e.target.value}); setAiModified({...aiModified, condition: true}); }}>
                      <option value="">Seleziona...</option>
                      {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="Da restaurare">Da restaurare</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label className="label">Prezzo acquisto €</label>
                    <input type="number" step="0.01" className="input" value={aiForm.prezzo_acquisto} onChange={e => setAiForm({...aiForm, prezzo_acquisto: e.target.value})} placeholder="0.00" />
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Prezzo vendita € {!aiModified.price && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <input type="number" step="0.01" className="input" value={aiForm.price} onChange={e => { setAiForm({...aiForm, price: e.target.value}); setAiModified({...aiModified, price: true}); }} />
                  </div>
                  <div>
                    <label className="label">Quantità</label>
                    <input type="number" className="input" value={aiForm.quantity} onChange={e => setAiForm({...aiForm, quantity: e.target.value})} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Colore {!aiModified.colore && aiForm.colore && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <input className="input" value={aiForm.colore} onChange={e => { setAiForm({...aiForm, colore: e.target.value}); setAiModified({...aiModified, colore: true}); }} />
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Taglia {!aiModified.taglia && aiForm.taglia && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                    <input className="input" value={aiForm.taglia} onChange={e => { setAiForm({...aiForm, taglia: e.target.value}); setAiModified({...aiModified, taglia: true}); }} />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Descrizione {!aiModified.description && aiForm.description && <span style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', borderRadius: 6, padding: '1px 6px', fontSize: '0.6rem' }}>🤖 AI</span>}</label>
                  <textarea className="input" rows={3} style={{ resize: 'none' }} value={aiForm.description} onChange={e => { setAiForm({...aiForm, description: e.target.value}); setAiModified({...aiModified, description: true}); }} />
                </div>
              </div>

              {/* RIGHT — Foto */}
              <div>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Foto caricate ({aiFotoPreviews.length})</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {aiFotoPreviews.map((prev, i) => (
                    <img key={i} src={prev} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 10 }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #2d1b69' }}>
              <button onClick={() => { setAiStep(1); setAiResult(null); }} className="btn-secondary" style={{ borderRadius: 9999 }}>← Rianalizza</button>
              <button onClick={handleAiSave} disabled={aiSaving || !aiForm.name} className="btn-primary"
                style={{ flex: 1, padding: 14, borderRadius: 16, justifyContent: 'center', opacity: aiSaving || !aiForm.name ? 0.5 : 1 }}>
                {aiSaving ? 'Salvataggio...' : '💾 Salva in Inventario'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sezione Archiviati */}
      {(data?.items || []).filter(p => p.status === 'archiviato').length > 0 && (
        <div style={{ marginTop: 32, padding: isMobile ? '0 16px' : 0 }}>
          <button onClick={() => setShowArchived(!showArchived)} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 12,
            background: showArchived ? 'rgba(167,139,250,0.1)' : T.elem, border: `1px solid ${showArchived ? T.violetDark : T.border}`,
            cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600,
            color: showArchived ? T.violet : T.muted, transition: '0.2s', marginBottom: 16,
          }}>
            📦 Prodotti Archiviati
            <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, background: 'rgba(167,139,250,0.12)', color: T.violet }}>
              {(data?.items || []).filter(p => p.status === 'archiviato').length}
            </span>
            <span style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>{showArchived ? '▲' : '▼'}</span>
          </button>
          {showArchived && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 8 : 12, opacity: 0.7 }}>
              {(data?.items || []).filter(p => p.status === 'archiviato').map((p) => (
                <div key={p.id} style={{ background: `rgba(107,114,128,0.2)`, borderRadius: 17, padding: 1 }}>
                  <div style={{ background: T.card, borderRadius: 16, overflow: 'hidden' }}>
                    <Link to={`/products/${p.id}`} style={{ textDecoration: 'none' }}>
                      <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', background: T.elem, overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
                        {p.foto_url ? <img src={`${API_BASE}${p.foto_url}`} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'grayscale(0.5)' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '2rem', opacity: 0.2 }}>📦</span></div>}
                        <span style={{ position: 'absolute', top: 10, left: 10, zIndex: 2, ...badgeBase, background: T.muted, color: '#fff' }}>📦 ARCHIVIATO</span>
                      </div>
                    </Link>
                    <div style={{ padding: 10 }}>
                      {p.brand && <p style={{ fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 700, color: T.muted, textTransform: 'uppercase' }}>{p.brand}</p>}
                      <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.muted }}>{p.name}</p>
                      <span style={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700, color: T.muted, textDecoration: 'line-through' }}>€{(p.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal conferma eliminazione/archiviazione */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,11,30,0.9)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 20, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: deleteConfirm.action === 'archivia' ? 'rgba(167,139,250,0.15)' : 'rgba(248,113,113,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '1.5rem' }}>
                {deleteConfirm.action === 'archivia' ? '📦' : '🗑️'}
              </div>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1.1rem', fontWeight: 700, color: T.text, marginBottom: 8 }}>
                {deleteConfirm.action === 'archivia' ? 'Archiviare questo prodotto?' : 'Eliminare questo prodotto?'}
              </h3>
              <p style={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.muted, margin: 0 }}>
                <strong style={{ color: T.text }}>{deleteConfirm.name}</strong>
              </p>
              {deleteConfirm.action !== 'archivia' && (
                <p style={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.red, marginTop: 8 }}>
                  Questa azione non può essere annullata
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                flex: 1, padding: '10px 0', borderRadius: 12, background: T.elem, border: `1px solid ${T.border}`,
                color: T.text, fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              }}>Annulla</button>
              <button onClick={() => {
                if (deleteConfirm.action === 'archivia') {
                  api.post(`/products/${deleteConfirm.id}/archivia`).then(() => { toast.success('Prodotto archiviato'); loadProducts(); }).catch(() => toast.error('Errore'));
                } else {
                  api.delete(`/products/${deleteConfirm.id}`).then(() => { toast.success('Prodotto eliminato'); loadProducts(); }).catch(() => toast.error('Errore'));
                }
                setDeleteConfirm(null);
              }} style={{
                flex: 1, padding: '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: deleteConfirm.action === 'archivia' ? T.gradPrimary : T.red,
                color: '#fff', fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 700,
              }}>
                {deleteConfirm.action === 'archivia' ? '📦 Archivia' : '🗑️ Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {annuncioAIProdotto && (
        <AnnuncioAIModal product={annuncioAIProdotto} onClose={() => setAnnuncioAIProdotto(null)} />
      )}
    </Layout>
  );
}
